import React, { useState } from 'react';
import {
  ChartBarIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

interface AnalyticsData {
  period: string;
  impressions: number;
  clicks: number;
  revenue: number;
  ctr: number;
  cpm: number;
  fillRate: number;
}

interface TopContent {
  id: string;
  title: string;
  type: string;
  impressions: number;
  clicks: number;
  revenue: number;
  ctr: number;
}

interface TopCampaign {
  id: string;
  name: string;
  advertiser: string;
  impressions: number;
  clicks: number;
  spent: number;
  ctr: number;
}

const Analytics: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('impressions');

  // Mock data - gerçek uygulamada API'den gelecek
  const analyticsData: AnalyticsData[] = [
    { period: '2024-01-15', impressions: 2500, clicks: 85, revenue: 320, ctr: 3.4, cpm: 128, fillRate: 92 },
    { period: '2024-01-14', impressions: 2200, clicks: 78, revenue: 295, ctr: 3.5, cpm: 134, fillRate: 89 },
    { period: '2024-01-13', impressions: 2800, clicks: 92, revenue: 380, ctr: 3.3, cpm: 136, fillRate: 94 },
    { period: '2024-01-12', impressions: 2100, clicks: 71, revenue: 275, ctr: 3.4, cpm: 131, fillRate: 88 },
    { period: '2024-01-11', impressions: 2600, clicks: 89, revenue: 350, ctr: 3.4, cpm: 135, fillRate: 91 },
    { period: '2024-01-10', impressions: 2400, clicks: 82, revenue: 310, ctr: 3.4, cpm: 129, fillRate: 90 },
    { period: '2024-01-09', impressions: 2300, clicks: 76, revenue: 285, ctr: 3.3, cpm: 124, fillRate: 87 },
  ];

  const topContent: TopContent[] = [
    {
      id: '1',
      title: 'Aksiyon Filmi - Hızlı ve Öfkeli 10',
      type: 'Film',
      impressions: 5200,
      clicks: 185,
      revenue: 680,
      ctr: 3.6
    },
    {
      id: '2',
      title: 'Komedi Dizisi - Güldür Güldür Show',
      type: 'Dizi',
      impressions: 4800,
      clicks: 162,
      revenue: 590,
      ctr: 3.4
    },
    {
      id: '3',
      title: 'Belgesel - Doğa ve Yaşam',
      type: 'Belgesel',
      impressions: 3200,
      clicks: 98,
      revenue: 420,
      ctr: 3.1
    },
    {
      id: '4',
      title: 'Canlı Yayın - Spor Maçı',
      type: 'Canlı',
      impressions: 6500,
      clicks: 234,
      revenue: 890,
      ctr: 3.6
    }
  ];

  const topCampaigns: TopCampaign[] = [
    {
      id: '1',
      name: 'Teknoloji Ürünleri',
      advertiser: 'TechCorp',
      impressions: 12500,
      clicks: 420,
      spent: 3200,
      ctr: 3.36
    },
    {
      id: '2',
      name: 'Moda ve Giyim',
      advertiser: 'FashionBrand',
      impressions: 8300,
      clicks: 285,
      spent: 2100,
      ctr: 3.43
    },
    {
      id: '3',
      name: 'Otomotiv Sektörü',
      advertiser: 'AutoBrand',
      impressions: 2100,
      clicks: 89,
      spent: 1200,
      ctr: 4.24
    }
  ];

  // Toplam değerleri hesapla
  const totalData = analyticsData.reduce((acc, curr) => ({
    impressions: acc.impressions + curr.impressions,
    clicks: acc.clicks + curr.clicks,
    revenue: acc.revenue + curr.revenue,
    ctr: 0, // Ortalama hesaplanacak
    cpm: 0, // Ortalama hesaplanacak
    fillRate: 0 // Ortalama hesaplanacak
  }), { impressions: 0, clicks: 0, revenue: 0, ctr: 0, cpm: 0, fillRate: 0 });

  totalData.ctr = (totalData.clicks / totalData.impressions) * 100;
  totalData.cpm = (totalData.revenue / totalData.impressions) * 1000;
  totalData.fillRate = analyticsData.reduce((acc, curr) => acc + curr.fillRate, 0) / analyticsData.length;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) {
      return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
    } else if (change < 0) {
      return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    }
    return null;
  };

  const getChangeColor = (change: number): string => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  // Mock değişim yüzdeleri
  const changes = {
    impressions: 12.5,
    clicks: 8.3,
    revenue: 15.2,
    ctr: 2.1,
    cpm: -3.4,
    fillRate: 5.7
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">Analitik ve Raporlar</h1>
          <p className="mt-1 text-sm text-gray-500">
            Reklam performansınızı detaylı olarak analiz edin
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="1d">Son 24 Saat</option>
            <option value="7d">Son 7 Gün</option>
            <option value="30d">Son 30 Gün</option>
            <option value="90d">Son 90 Gün</option>
          </select>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <FunnelIcon className="-ml-1 mr-2 h-4 w-4" />
            Filtrele
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EyeIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Toplam Gösterim
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatNumber(totalData.impressions)}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${getChangeColor(changes.impressions)}`}>
                      {getChangeIcon(changes.impressions)}
                      <span className="ml-1">
                        {Math.abs(changes.impressions)}%
                      </span>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CursorArrowRaysIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Toplam Tıklama
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatNumber(totalData.clicks)}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${getChangeColor(changes.clicks)}`}>
                      {getChangeIcon(changes.clicks)}
                      <span className="ml-1">
                        {Math.abs(changes.clicks)}%
                      </span>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Toplam Gelir
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totalData.revenue)}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${getChangeColor(changes.revenue)}`}>
                      {getChangeIcon(changes.revenue)}
                      <span className="ml-1">
                        {Math.abs(changes.revenue)}%
                      </span>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    CTR
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {totalData.ctr.toFixed(2)}%
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${getChangeColor(changes.ctr)}`}>
                      {getChangeIcon(changes.ctr)}
                      <span className="ml-1">
                        {Math.abs(changes.ctr)}%
                      </span>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    CPM
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(totalData.cpm)}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${getChangeColor(changes.cpm)}`}>
                      {getChangeIcon(changes.cpm)}
                      <span className="ml-1">
                        {Math.abs(changes.cpm)}%
                      </span>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Doluluk Oranı
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {totalData.fillRate.toFixed(1)}%
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${getChangeColor(changes.fillRate)}`}>
                      {getChangeIcon(changes.fillRate)}
                      <span className="ml-1">
                        {Math.abs(changes.fillRate)}%
                      </span>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Placeholder */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Performans Trendi</h3>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="impressions">Gösterimler</option>
              <option value="clicks">Tıklamalar</option>
              <option value="revenue">Gelir</option>
              <option value="ctr">CTR</option>
            </select>
          </div>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                Grafik entegrasyonu yakında eklenecek
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Content and Campaigns */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Top Performing Content */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">En İyi Performans Gösteren İçerikler</h3>
            <div className="space-y-4">
              {topContent.map((content, index) => (
                <div key={content.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                        {content.title}
                      </p>
                      <p className="text-xs text-gray-500">{content.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatNumber(content.impressions)} gösterim
                    </p>
                    <p className="text-xs text-gray-500">
                      {content.ctr.toFixed(2)}% CTR • {formatCurrency(content.revenue)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Campaigns */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">En İyi Performans Gösteren Kampanyalar</h3>
            <div className="space-y-4">
              {topCampaigns.map((campaign, index) => (
                <div key={campaign.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-green-600">{index + 1}</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                        {campaign.name}
                      </p>
                      <p className="text-xs text-gray-500">{campaign.advertiser}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatNumber(campaign.impressions)} gösterim
                    </p>
                    <p className="text-xs text-gray-500">
                      {campaign.ctr.toFixed(2)}% CTR • {formatCurrency(campaign.spent)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;